import {useEffect, useState} from 'react';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useLocation, useNavigate} from 'react-router-dom';
import {alpha, useTheme} from '@mui/material/styles';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {dismissFrontDeskIntro, isFrontDeskIntroDismissed} from 'src/utils/contextPreferences';

interface FrontDeskIntroPopoverProps {
  anchorEl: HTMLElement | null;
  enabled: boolean;
}

export function FrontDeskIntroPopover({anchorEl, enabled}: FrontDeskIntroPopoverProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const account = useAppSelector(selectAccount);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !account.userId || !anchorEl) {
      setOpen(false);
      return;
    }
    if (location.pathname.startsWith('/front-desk') || isFrontDeskIntroDismissed(account.userId)) {
      setOpen(false);
      return;
    }
    const timer = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(timer);
  }, [account.userId, anchorEl, enabled, location.pathname]);

  const handleDismiss = () => {
    if (account.userId) dismissFrontDeskIntro(account.userId);
    setOpen(false);
  };

  const handleOpenFrontDesk = () => {
    handleDismiss();
    navigate('/front-desk');
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleDismiss}
      anchorOrigin={{vertical: 'center', horizontal: 'right'}}
      transformOrigin={{vertical: 'center', horizontal: 'left'}}
      marginThreshold={12}
      slotProps={{
        paper: {
          sx: {
            ml: 1.25,
            width: 280,
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          },
        },
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 0.75,
            py: 0.25,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          New
        </Box>
        <IconButton
          size="small"
          aria-label="Dismiss Front Desk introduction"
          onClick={handleDismiss}
          sx={{mt: -0.5, mr: -0.75, color: 'text.secondary'}}
        >
          <CloseIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>
      <Typography variant="subtitle1" sx={{mt: 1, fontWeight: 700, letterSpacing: '-0.02em'}}>
        Front Desk
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, lineHeight: 1.5}}>
        Call residents and open doors from one place in the portal.
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={handleOpenFrontDesk}
        sx={{mt: 1.75, textTransform: 'none', fontWeight: 600}}
      >
        Open Front Desk
      </Button>
    </Popover>
  );
}
